/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { dataProviderRegistry } from './form/services/DataProviderRegistry';
import { Dialog, Theme, useMediaQuery } from "@material-ui/core";
import { createStyles, makeStyles, useTheme } from "@material-ui/styles";
import React, { useEffect, useState } from "react";
import ManagerSelect, { NEW_MANAGER_NAME } from "./ManagerSelect";
import { v4 as uuidv4 } from 'uuid';
import OwnerSelect, { NEW_OWNER_NAME } from './OwnerSelect';
import FacilityGroupSelect, { FacilityGroupQueryParams } from './FacilityGroupSelect';

interface UserQueryParams {
  load: "current_user" | "list"
  certifications: string[]
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    editorOverlayPaper: {
      height: "100%"
    },
    editorOverlay: {
      height: "calc(100%)"
    }
  })
);

const EvalOverlay = () => {
  const [DialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [cancelHandler, setCancelHandler] = useState<() => void>();

  const handleSelectClose = () => {
    setDialogContent(null);
  };

  const theme: Theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const classes = useStyles({});

  useEffect(function setupDataProviders() {
    dataProviderRegistry.register("manager", {
      getData() {
        const p = new Promise<object | null>(function (resolve, reject) {
          setDialogContent(
            <ManagerSelect
              onSelect={selection => {
                if (selection) {
                  if (selection.name !== NEW_MANAGER_NAME) {
                    resolve({ ...selection, new: false });
                  } else {
                    resolve({ id: uuidv4(), new: true, name: null, company: null, phone: null, email: null, subscribed: true });
                  }
                } else {
                  resolve(selection);
                }
                handleSelectClose();
              }}
            />
          );

          setCancelHandler(() => () => resolve(null));
        });
        return p;
      }
    });

    dataProviderRegistry.register("owner", {
      getData() {
        const p = new Promise<object | null>(function (resolve, reject) {
          setDialogContent(
            <OwnerSelect
              onSelect={selection => {
                if (selection) {
                  if (selection.name !== NEW_OWNER_NAME) {
                    resolve({ ...selection, new: false, subscribed: selection.subscribed });
                  } else {
                    resolve({ id: uuidv4(), new: true, name: null, company: null, phone: null, email: null, subscribed: true });
                  }
                } else {
                  resolve(selection);
                }
                handleSelectClose();
              }}
            />
          );

          setCancelHandler(() => () => resolve(null));
        });
        return p;
      }
    });

    dataProviderRegistry.register("facilityGroup", {
      getData(params: FacilityGroupQueryParams) {
        const p = new Promise<object | null>(function (resolve, reject) {
          setDialogContent(
            <FacilityGroupSelect
              onSelect={selection => {
                resolve(selection);
                handleSelectClose();
              }}
            />
          );

          setCancelHandler(() => () => resolve(null));
        });
        return p;
      }
      // Set the active facility in the appState
      // Read that when we call 
    });
  }, []);

  return (
    <Dialog
      id="eval-editor-overlay"
      open={DialogContent !== null}
      maxWidth="sm"
      fullWidth
      onClose={reason => {
        if (cancelHandler) cancelHandler();
        handleSelectClose();
      }}
      fullScreen={fullScreen}
      className={classes.editorOverlay}
      classes={{
        paper: classes.editorOverlayPaper
      }}
    >
      {DialogContent || <div />}
    </Dialog>
  );
};

export default EvalOverlay;
